/**
 * Week and to-date line km per block from the reviewed line status (ADR-013). This is the only source of km for
 * every report: only `accepted_m` of reviewed (non-pending), `use_km = true`, not deleted statuses counts. Line
 * lists hold every reviewed line flown (rejected ones included, as the weekly report lists them), and `rejected`
 * lists the rejected lines flown in the week in flight order.
 *
 * The flight's date decides the week (ISO, Monday to Sunday) and whether it is "to date": full dates, never day
 * numbers, so December's km stay in "to date" after 1 January, week 53 holds its January days, and year 1's day 289
 * is not year 2's. A flight that touched two blocks contributes to both through its lines' blocks.
 */
import { inWeek, isoDate, jdayToDate, weekDates } from './dates.js';
import { statusRows } from './km.js';
import { blockPlannedKm } from './reflights.js';
import type { BlockPlan, RejectedLine, StatusRow } from './km.js';
import { parseLineNumber } from './lines.js';
import type { Block, Flight, LineStatus, PlannedLine } from './model.js';

export interface ReportPeriod {
  /** ISO week of the report. */
  readonly week: number;
  /** ISO year of the week (week 53 of 2026 ends on 3 January 2027). */
  readonly year: number;
  /** Last date to include (`YYYY-MM-DD`, e.g. the daily report's date); defaults to the week's Sunday. */
  readonly toDate?: string;
  /** Last day of `year` to include, for callers that only know the day number; `toDate` is preferred. */
  readonly toDay?: number;
}

export interface ReportTotals {
  readonly weekKm: Readonly<Record<string, number>>;
  readonly todateKm: Readonly<Record<string, number>>;
  readonly weekLines: Readonly<Record<string, readonly number[]>>;
  readonly todateLines: Readonly<Record<string, readonly number[]>>;
  readonly rejected: readonly RejectedLine[];
  /** Project Status rows (survey blocks only), to-date at the end of the period. */
  readonly statusRows: readonly StatusRow[];
  readonly weekTotalKm: number;
  readonly todateTotalKm: number;
}

/** Keys are `Block.key`. `flights` supplies each status's date (via `flight_id`). */
export function reportTotals(
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  blocks: readonly Block[],
  flights: readonly Flight[],
  period: ReportPeriod,
): ReportTotals {
  const week = weekDates(period.year, period.week);
  const asked =
    period.toDate ?? (period.toDay ? isoDate(jdayToDate(period.year, period.toDay)) : week.sunday);
  const last = asked < week.sunday ? asked : week.sunday;
  const flightById = new Map(flights.map((f) => [f.id, f]));
  const blockById = new Map(blocks.map((b) => [b.id, b]));
  const blockByDigit = new Map(blocks.filter((b) => b.kind === 'survey').map((b) => [b.digit, b]));
  const planById = new Map(plan.map((p) => [p.id, p]));

  const weekKm: Record<string, number> = {};
  const todateKm: Record<string, number> = {};
  const weekLines: Record<string, Set<number>> = {};
  const todateLines: Record<string, Set<number>> = {};
  for (const b of blocks) {
    weekKm[b.key] = 0;
    todateKm[b.key] = 0;
    weekLines[b.key] = new Set();
    todateLines[b.key] = new Set();
  }

  const blockOf = (s: LineStatus): Block | undefined => {
    const p = planById.get(s.planned_line_id);
    const viaPlan = p ? blockById.get(p.block_id) : undefined;
    if (viaPlan) return viaPlan;
    const digit = parseLineNumber(s.line_number).blockDigit;
    return digit === null ? undefined : blockByDigit.get(digit);
  };

  const rows = statuses
    .filter((s) => s.deleted_at === null && s.acceptance !== 'pending')
    .map((s) => ({ s, f: flightById.get(s.flight_id), b: blockOf(s) }))
    .filter(
      (r): r is { s: LineStatus; f: Flight; b: Block } =>
        r.f !== undefined && r.b !== undefined && r.f.date <= last,
    )
    .sort(
      (x, y) =>
        x.f.date.localeCompare(y.f.date) || x.f.flt_num - y.f.flt_num || x.s.line_number - y.s.line_number,
    );

  const rejected: RejectedLine[] = [];
  for (const { s, f, b } of rows) {
    const km = s.use_km ? s.accepted_m / 1000 : 0;
    todateLines[b.key]!.add(s.line_number);
    todateKm[b.key]! += km;
    if (inWeek(f.date, week)) {
      weekLines[b.key]!.add(s.line_number);
      weekKm[b.key]! += km;
      if (s.acceptance === 'rejected') rejected.push({ line: s.line_number, reason: s.reason });
    }
  }

  const sorted = (m: Record<string, Set<number>>): Record<string, number[]> =>
    Object.fromEntries(Object.entries(m).map(([k, v]) => [k, [...v].sort((a, b) => a - b)]));
  const plans: BlockPlan[] = blocks
    .filter((b) => b.kind === 'survey')
    .map((b) => ({
      key: b.key,
      name: b.name,
      digit: b.digit,
      plannedKm: blockPlannedKm(b, plan),
      plannedLen: {},
    }));
  const total = (m: Record<string, number>): number => Object.values(m).reduce((a, b) => a + b, 0);
  return {
    weekKm,
    todateKm,
    weekLines: sorted(weekLines),
    todateLines: sorted(todateLines),
    rejected,
    statusRows: statusRows(plans, todateKm),
    weekTotalKm: total(weekKm),
    todateTotalKm: total(todateKm),
  };
}
